<template>
    <tailwind-layout title="Tenant Screening">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"><!-- Block for lg and larger -->
                <div class="flex flex-col">
                    <h1 class="font-semibold text-2xl leading-tight">{{ $page.props.auth.user.name }}'s Tenant Screening</h1>
                    <h2 class="text-xs">This is where you manage properties and applications for tenant screening and verified screening.</h2>
                </div>
                <div class="flex items-center space-x-4">
                    <inertia-link data-demo-target="add-property-btn" class="primaryButton" :href="route('properties.create')">
                        Add Property
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                    <div class="flex justify-between items-start md:items-center">
                    <div class="flex flex-col">
                        <h1 class="font-semibold text-2xl leading-tight">{{ $page.props.auth.user.name }}'s Tenant Screening</h1>
                        <h2 class="text-xs">This is where you manage properties and applications for tenant screening and verified screening.</h2>
                    </div>
                    <inertia-link data-demo-target="add-property-btn" class="primaryButton" :href="route('properties.create')">
                        Add Property
                    </inertia-link>
                </div>
            </div>
        </template>
        
        <!-- Share Property Modal -->
        <share-modal 
            :show="showPropertyShareModal"
            :share-url="selectedProperty ? selectedProperty.application_link : ''"
            :share-text="selectedProperty ? `${selectedProperty.address_1} is available for rent in ${selectedProperty.city}, ${selectedProperty.state}. Apply here today!` : ''"
            title="Share Property Application Link"
            @close="closeShareModal"
        />
        
        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch ml-2 lg:ml-0">
                    <div class="w-full sm:w-2/12 pr-4 order-2 sm:order-1">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of Properties per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="w-full col-span-2 sm:w-2/12 pr-4 order-2 sm:order-1 mt-1 text-right sm:hidden">
                        <a data-demo-target="download-applicants-btn" target="_blank" :href="route('download.applicants')" class="whiteButton">
                            <font-awesome-icon icon="fa-download" class="pr-2" /> Download All Applicants
                        </a>
                    </div>
                    <div class="col-span-3 sm:w-6/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <input id="search" v-model="form.search" type="text" placeholder="Search for Properties..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md sm:mx-2">
                        <div class="flex items-center mt-2 md:ml-2" @click="show_archived_properties()">
                            <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                    <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                </Switch>
                                <SwitchLabel as="span" class="ml-3 text-sm">
                                    <span class="font-medium text-gray-700">Show Archived Properties</span>
                                </SwitchLabel>
                            </SwitchGroup>
                        </div>
                    </div>
                    <div class="sm:w-4/12 order-4 sm:order-4 hidden sm:flex items-start justify-end">
                        <a data-demo-target="download-applicants-btn" target="_blank" :href="route('download.applicants')" class="whiteButton">
                            <font-awesome-icon icon="fa-download" class="pr-2" /> Download All Applicants
                        </a>
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table data-demo-target="properties-table" class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Address
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-right">
                                            Share Property
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-right">
                                            <span @click="sortTable('rent')">
                                                Rent
                                                <icon v-show="form.sort_by === 'rent'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'rent'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-right">
                                            Active Applications
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-right">
                                            <span @click="sortTable('status')">
                                                Status
                                                <icon v-show="form.sort_by === 'status'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'status'" :name="'sort'" />
                                            </span>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200">
                                    <tr v-for="(property, index) in properties.data" :key="index"
                                           :class="[(index % 2 === 0 && !property.deleted_at ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : (!property.deleted_at) ? 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer' : ''),
                                        (property.deleted_at ? 'bg-red-100 !border-b !border-red-200 hover:bg-red-200 hover:cursor-pointer' : '')]"
                                        @click="show_property_page(property.id)"
                                    >
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ property.full_address }}
                                        </td>
                                        <td class="whitespace-nowrap text-right">
                                            <button @click.stop="openShareModal(property)" title="Copy link to clipboard" class="py-4 pl-4 pr-3 w-full text-right"><font-awesome-icon :icon="faArrowUpRightFromSquare" class="text-blue-500 hover:text-blue-700" /></button>
                                        </td>
                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                            {{ property.rent }}
                                        </td>
                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                            {{ property.active_applications }}
                                        </td>
                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                            <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                 <span :class="[property.status === 'Active' ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-red-700 bg-red-50 ring-red-600/10', 'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset' ]">
                                                     {{ property.status }}
                                                 </span>
                                            </span>
                                            <span v-if="property.deleted_at" class="text-red-700 bg-red-50 ring-red-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset">
                                                Archived
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-show="!properties.data.length">
                                        <td colspan="5" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No Properties were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(property, index) in properties.data" :key="index" :class="[property.deleted_at ? 'bg-red-100 hover:bg-red-50 hover:outline-red-500 divide-red-300' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border']">
                                        <inertia-link :href="route('properties.show', property.id)">
                                        <div class="flex flex-col p-6">
                                            <div class="flex items-center space-x-2 mb-1">
                                                <h3 class="truncate text-sm font-medium text-gray-900">{{ property.full_address }}</h3>
                                                <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full flex-shrink-0">
                                                    <span :class="[property.status === 'Active' ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-red-700 bg-red-50 ring-red-600/10', 'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset']">
                                                        {{ property.status }}
                                                    </span>
                                                </span>
                                            </div>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Rent:</span> {{ property.rent }}</h4>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Applications:</span> {{ property.active_applications }}</h4>
                                        </div>
                                        </inertia-link>
                                        <div>
                                            <div :class="[property.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                <div class="flex w-0 flex-1">
                                                    <inertia-link :href="route('properties.show', property.id)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-eyes" />
                                                        View
                                                    </inertia-link>
                                                </div>
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <inertia-link :href="route('properties.edit', property.id)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-edit" />
                                                        Edit
                                                    </inertia-link>
                                                </div>
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <button 
                                                        @click.stop="openShareModal(property)"
                                                        title="Copy link to clipboard"
                                                        class="hover:bg-blue-200 relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900"
                                                    >
                                                        <font-awesome-icon :icon="faArrowUpRightFromSquare" class="text-gray-900" /> Share
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="properties.links" :results="properties" :type="'properties'" />
        </div>
        
        <!-- Demo Widget -->
        <DemoWidget
            :page-identifier="'properties.index'"
            :steps="demoSteps"
            :auto-start="true"
            :show-help-icon="true"
        />
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import {Switch, SwitchLabel, SwitchGroup} from "@headlessui/vue";
import DemoWidget from '@/Components/DemoWidget.vue'
import { getDemoSteps } from '@/Config/demoSteps.js'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faArrowUpRightFromSquare, faCircleX } from "@fortawesome/pro-solid-svg-icons";
import { faCheckCircle, faXmark } from "@fortawesome/pro-light-svg-icons";
import ShareModal from './ShareModal.vue';

export default {
    components: {
        SwitchGroup,
        SwitchLabel,
        Switch,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        DemoWidget,
        FontAwesomeIcon,
        ShareModal
    },
    props: {
        filters: Object,
        properties: Object,
        people: Object,
    },
    data() {
        return {
            faArrowUpRightFromSquare,
            faCheckCircle,
            faCircleX,
            faXmark,
            form: {
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:100,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            demoSteps: getDemoSteps('properties.index'),
            showPropertyShareModal: false,
            selectedProperty: null,
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('properties.index'), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        show_property_page(id) {
            this.$inertia.get(this.route('properties.show',id), pickBy(this.form), { preserveState: false })
        },
        show_archived_properties() {
            this.form.trashed = !this.form.trashed;
        },
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        openShareModal(property) {
            this.selectedProperty = property;
            console.log('Selected Property:', this.selectedProperty);
            this.showPropertyShareModal = true;
        },
        closeShareModal() {
            this.showPropertyShareModal = false;
            this.selectedProperty = null;
        },
    },
}
</script>
